import { registerPlugin } from '@capacitor/core';
import { Hub } from 'aws-amplify/utils';

const AppleSignIn = registerPlugin('AppleSignIn');

// Must match aws_user_pools_web_client_id in aws-exports.js — Amplify v6 keys its
// token store under CognitoIdentityServiceProvider.<clientId>.*, so injecting under
// the wrong id leaves fetchAuthSession() blind to the session we just minted.
const COGNITO_CLIENT_ID = '778t4bsqj0taofju80rv36bn1b';
const API_ENDPOINT =
  process.env.REACT_APP_API_ENDPOINT ||
  'https://jeabwk7au0.execute-api.eu-west-2.amazonaws.com/braingrid';

function decodeJwtPayload(jwt) {
  if (typeof jwt !== 'string') throw new Error('Invalid token: not a string');
  const parts = jwt.split('.');
  if (parts.length !== 3) throw new Error('Invalid token: malformed JWT');
  try {
    return JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    throw new Error('Invalid token: payload not base64-decodable JSON');
  }
}

function injectAmplifyTokens({ idToken, accessToken, refreshToken }) {
  if (!idToken || !accessToken || !refreshToken) {
    throw new Error('Apple sign-in: backend response missing tokens');
  }
  const payload = decodeJwtPayload(idToken);
  const username = payload['cognito:username'] || payload.email;
  if (!username) {
    throw new Error('Apple sign-in: token has no cognito:username or email claim');
  }
  const prefix = `CognitoIdentityServiceProvider.${COGNITO_CLIENT_ID}`;
  localStorage.setItem(`${prefix}.LastAuthUser`, username);
  localStorage.setItem(`${prefix}.${username}.idToken`, idToken);
  localStorage.setItem(`${prefix}.${username}.accessToken`, accessToken);
  localStorage.setItem(`${prefix}.${username}.refreshToken`, refreshToken);
  localStorage.setItem(`${prefix}.${username}.clockDrift`, '0');
}

/**
 * Native Apple Sign In on iOS (ASAuthorizationController) → exchange the identity
 * token with the backend for a Cognito session → inject tokens into Amplify's
 * localStorage store so fetchAuthSession() sees the user. Throws on cancel/failure.
 */
export async function nativeAppleSignIn() {
  const { identityToken, fullName } = await AppleSignIn.signIn();

  const res = await fetch(`${API_ENDPOINT}/auth/apple-native`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ identityToken, fullName: fullName || null }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Apple sign-in failed (${res.status})`);
  }

  const tokens = await res.json();
  injectAmplifyTokens(tokens);

  // Nudge any Amplify Hub listeners; harmless if none are registered. The event
  // name must be 'signedIn' to match aws-amplify v6.
  Hub.dispatch('auth', { event: 'signedIn' });

  return tokens;
}
